import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { usePodcast } from "@/contexts/PodcastContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function PodcastSelector() {
  const { selectedPodcast, setSelectedPodcast, availablePodcasts, isLoading } = usePodcast();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700", theme.borders.neubrutalism, "rounded-lg font-bold text-gray-900 dark:text-white", theme.shadows.neubrutalism2)}>
        <span className="text-xl">🎧</span>
        <span className="hidden sm:inline">Loading...</span>
      </div>
    );
  }

  if (!selectedPodcast || availablePodcasts.length === 0) {
    return (
      <Link
        to="/podcasts/add"
        className={cn(
          "flex items-center gap-2 px-4 py-2 rounded-lg font-bold",
          theme.buttons.primary,
        )}
      >
        <span className="text-xl">➕</span>
        <span className="hidden sm:inline">Add a podcast</span>
        <span className="sm:hidden">Add</span>
      </Link>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        title={selectedPodcast.name}
        className={cn(
          "flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors font-bold text-gray-900 dark:text-white",
          theme.borders.neubrutalism,
          theme.shadows.neubrutalism2,
          "max-w-[200px] sm:max-w-[300px] md:max-w-[400px]",
        )}
      >
        <span className="text-xl flex-shrink-0">{selectedPodcast.emoji}</span>
        <span className="truncate min-w-0 text-sm sm:text-base" title={selectedPodcast.name}>
          {selectedPodcast.name}
        </span>
        <span className={cn("text-xs sm:text-sm px-1.5 sm:px-2 py-0.5 rounded-full font-bold flex-shrink-0", theme.colors.primaryOrange, "text-gray-900 dark:text-white")}>
          {selectedPodcast.episodeCount ?? "..."}
        </span>
        <svg className={cn("w-4 h-4 transition-transform flex-shrink-0", isOpen ? "rotate-180" : "")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={cn(
          "absolute top-full left-0 mt-2 w-full min-w-[200px] sm:min-w-[280px] bg-white dark:bg-gray-700",
          theme.borders.neubrutalism,
          "rounded-lg",
          theme.shadows.neubrutalism,
          "z-50 max-h-[60vh] overflow-y-auto"
        )}>
          {availablePodcasts.map(podcast => (
            <button
              key={podcast.id}
              onClick={() => {
                setSelectedPodcast(podcast);
                setIsOpen(false);
              }}
              title={podcast.name}
              className={cn(
                "w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left",
                selectedPodcast.id === podcast.id ? "bg-gray-100 dark:bg-gray-600" : "",
              )}
            >
              <span className="text-xl flex-shrink-0">{podcast.emoji}</span>
              <span className="font-medium text-gray-900 dark:text-white truncate min-w-0 flex-1" title={podcast.name}>
                {podcast.name}
              </span>
              {selectedPodcast.id === podcast.id && (
                <svg className={`w-4 h-4 ml-auto flex-shrink-0 text-primary-orange dark:text-primary-orange-dark`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
